/**
 * Copyright(c) 2025 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module AlgoliaServerActions
 *
 * @author Naseef O
 * (copied from MAG repo to DRM repo)
 *
 */
"use server";

import { algoliaCache } from "../../apiUtils/InternalCacheHandler";
import { algoliaCacheOptions } from "../../runtimeConfig/server";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { recommendClient as createRecommendClient, RecommendationsRequest } from "@algolia/recommend";
import { MultipleQueries } from "./types";
import { algoliaConfig } from "../../runtimeConfig/server";

const { applicationID, searchApiKey } = algoliaConfig || {};

/** Creating Algolia search client */
const searchClient = algoliasearch(applicationID, searchApiKey);
const recommendClient = createRecommendClient(applicationID, searchApiKey);

/**
 * Caches and retrieves search results from Algolia.
 * @param {SearchClient} searchClient - Initialized Algolia search client
 * @param {MultipleQueries[]} searchRequest - Array of search queries to send to Algolia
 * @returns {Promise<MultipleQueriesResponse[]>} - Array of search responses from Algolia
 */
export const algoliaSearchCached = async (searchRequest: MultipleQueries[], cacheMetadata: { [key: string]: any }) => {
  let algoliaResponse;
  const cacheKey = {
    searchRequest,
    cacheMetadata,
    tag: "algolia-instant-search",
  };

  const fetchData = async () => {
    const response = await searchClient.search({ requests: searchRequest });
    return response;
  };

  if (!algoliaCacheOptions?.disabled) {
    try {
      console.log("Reading from Algolia Cache");
      algoliaResponse = await algoliaCache.get(cacheKey, fetchData);
    } catch (error) {
      console.error("Error while fetching from algolia-cache for search request", searchRequest);
      console.dir(error, { depth: null });
      algoliaResponse = {};
    }
  } else {
    try {
      console.log("fetching from algolia remote for request", searchRequest);
      algoliaResponse = await fetchData();
    } catch (error) {
      console.error("Error while fetching from algolia for search request", searchRequest);
      console.dir(error, { depth: null });
      algoliaResponse = {};
    }
  }

  return algoliaResponse;
};
/**
 * Caches and retrieves recommendations from Algolia.
 * @param {RecommendationsRequest[]} reqObj - Array of recommendation queries to send to Algolia.
 * @returns {Promise<import('@algolia/recommend').RecommendationsResponse>} - The recommendations response from Algolia.
 */
export const algoliaRecommendationsCached = async (reqObj: RecommendationsRequest[], cacheMetadata: { [key: string]: any }) => {
  let algoliaResponse;
  const cacheKey = {
    reqObj,
    cacheMetadata,
    tag: "algolia-recommendation-search",
  };

  const fetchData = async () => {
    const response = await recommendClient.getRecommendations({ requests: reqObj });
    return response;
  };

  if (!algoliaCacheOptions?.disabled) {
    try {
      console.log("Reading from Algolia Cache");
      algoliaResponse = await algoliaCache.get(cacheKey, fetchData);
    } catch (error) {
      console.error("Error while fetching from algolia-cache for recommendationrequest", reqObj);
      console.dir(error, { depth: null });
      algoliaResponse = {};
    }
  } else {
    try {
        console.log("fetching from algolia remote for request", reqObj);
        algoliaResponse = await fetchData();
    } catch (error) {
      console.error("Error while fetching from algolia for request", reqObj);
      console.dir(error, { depth: null });
      algoliaResponse = {};
    }
  }

  return algoliaResponse;
};
